import { useApi } from '@/api/useApi.ts';
import { IChatMessage } from '@/interfaces/deltaGpt/IChatMessage.ts';
import {
    IPdfDetails, IDocumentSimple, IPowerBiDetails, IExcelDetails,
    IAdminPDFDocument,
    IAdminPowerBi,
    IAdminExcelDocument,
    IAdminSparkContent,
    IDocumentFilterOrderResponse,
    ISparkContentDetails,
    ReportType,
    IBulkDocumentAccessPayload,
} from '@/interfaces/deltaGpt/IDocument.ts';
import {
    IDeltaUserSettings,
    IFeedVisitState,
    IUserTrackingData,
} from '@/interfaces/deltaGpt/IDeltaUserSettings.ts';
import { IFeedback } from '@/interfaces/deltaGpt/IFeedback.ts';
import { ITransposeAccessDetails } from '@/interfaces/deltaGpt/ITransposeAccessDetails.ts';
import { IGetExpertsPayload } from '@/interfaces/deltaGpt/IGetExpertsPayload.ts';
import { IChatToExpertsPayload } from '@/interfaces/deltaGpt/IChatToExpertsPayload.ts';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { authState } from '@lcp/http-auth';
import { IGenerationOption } from '@lcp/transpose';
import { IGenerationOptionWithDocumentCode } from '@/interfaces/deltaGpt/IGenerationOptionWithDocumentCode.ts';
import { IShareDocumentPayload } from '@/interfaces/deltaGpt/IShareDocumentPayload.ts';
import { ISharedDocumentInfo } from '@/interfaces/deltaGpt/ISharedDocumentInfo.ts';
import { IAnalyst, IAnalystAdmin, ICreateAnalyst } from '@/interfaces/deltaGpt/IAnalyst.ts';
import { IResearchAccess } from '@/interfaces/auth/IResearchAccess.ts';
import {
    IDocumentComment,
    IDocumentCommentCreateRequest,
    IDocumentCommentUpdateRequest,
    IDocumentCommentVoteRequest,
} from '@/interfaces/deltaGpt/IDocumentComment.ts';
import {
    IRichTextImageUploadPayload,
    IRichTextPendingImage,
} from '@/interfaces/deltaGpt/IRichTextImage.ts';
import {
    IRichTextPayloadWithPendingImages,
    IRichTextPayloadWithUploadImages,
} from '@/interfaces/deltaGpt/IRichTextPayload.ts';

import { IAdminAccessInfo } from '@/interfaces/deltaGpt/IAdminAccessInfo.ts';
import { ISparkDefinitionSearchInfo } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import { IUserAccessSummaryResponse } from '@/interfaces/deltaGpt/IUserAccessSummary.ts';

const apiCall = useApi('deltaGpt');
const accessApiCall = useApi('accessApi');

const CONTACT_US_BUTTON_HTML = '<button onclick="window.showContactDialog()" class="contact-button">Contact Us</button>';
const NO_CHAT_ACCESS_TEXT = `You must have an account to chat with our research. If you have an account, please log in using the button in the header above. If not, please contact us to gain access: ${CONTACT_US_BUTTON_HTML}`;

async function crossDocumentChat (conversationId: string, messages: Array<IChatMessage>, callback: (text: string) => void): Promise<void> {
    const endpoint = '/CrossDocumentChat/';
    const decoder = new TextDecoder('utf-8');

    if (authState.value.loggedIn) {
        await apiCall.postStream({ endpoint, payload: { messages, conversationId } }, (val: Uint8Array) => {
            callback(decoder.decode(val, { stream: true }).replace(/contact us/gi, CONTACT_US_BUTTON_HTML));
        });
    } else {
        callback(NO_CHAT_ACCESS_TEXT);
    }
}

async function documentChat (documentShortCode: string, conversationId: string, messages: Array<IChatMessage>, callback: (text: string) => void): Promise<void> {
    const endpoint = `/DocumentChat/${documentShortCode}`;
    const decoder = new TextDecoder('utf-8');

    if (authState.value.loggedIn) {
        await apiCall.postStream({ endpoint, payload: { messages, conversationId } }, (val: Uint8Array) => {
            callback(decoder.decode(val, { stream: true }).replace(/contact us/gi, CONTACT_US_BUTTON_HTML));
        });
    } else {
        callback(NO_CHAT_ACCESS_TEXT);
    }
}

async function getDocumentReferences (documentShortCode: string): Promise<string> {
    const endpoint = authState.value.loggedIn ? `/GetPdfText/${documentShortCode}` : `/GetPdfTextPublic/${documentShortCode}`;
    return (await apiCall.get<string>({ endpoint })).data;
}

async function getPageReferences (documentShortCode: string, page: number): Promise<string> {
    const endpoint = `/GetPageText/${documentShortCode}/${page}`;
    return (await apiCall.get<string>({ endpoint })).data;
}

async function getPdf (documentShortCode: string, shareId: string | undefined): Promise<Uint8Array> {
    let endpoint;
    if (shareId) {
        endpoint = authState.value.loggedIn ? `/GetPdfFromSharedLink/${documentShortCode}/${shareId}` : `/GetPdfFromSharedLinkPublic/${documentShortCode}/${shareId}`;
    } else {
        endpoint = authState.value.loggedIn ? `/GetPdf/${documentShortCode}` : `/GetPdfPublic/${documentShortCode}`;
    }
    return (await apiCall.get<Uint8Array>({ endpoint, extraScopeOptions: { responseType: 'arrayBuffer' } })).data;
}

async function getPdfPage (documentShortCode: string, page: number): Promise<Uint8Array> {
    const endpoint = `/GetPdfPage/${documentShortCode}/${page}`;
    return (await apiCall.get<Uint8Array>({ endpoint, extraScopeOptions: { responseType: 'arrayBuffer' } })).data;
}

async function getAllDocumentInfo (): Promise<Array<IDocumentSimple>> {
    return getAllDocumentInfoByType();
}

async function getAllDocumentInfoByType (reportTypes?: Array<ReportType>): Promise<Array<IDocumentSimple>> {
    const baseEndpoint = authState.value.loggedIn ? '/GetAllDocumentInfo' : '/GetAllDocumentInfoPublic';
    const query = buildReportTypeQuery(reportTypes);
    const endpoint = query.length > 0 ? `${baseEndpoint}?${query}` : baseEndpoint;
    return (await apiCall.get<Array<IDocumentSimple>>({ endpoint })).data;
}

async function getDocumentComments (documentShortCode: string): Promise<Array<IDocumentComment>> {
    const endpoint = authState.value.loggedIn ? `/GetDocumentComments/${documentShortCode}` : `/GetDocumentCommentsPublic/${documentShortCode}`;
    return (await apiCall.get<Array<IDocumentComment>>({ endpoint })).data;
}

async function createDocumentComment (documentShortCode: string, payload: IDocumentCommentCreateRequest): Promise<IDocumentComment> {
    const requestPayload = await prepareRichTextImagePayload(payload);
    return (await apiCall.post<typeof requestPayload, IDocumentComment>({ endpoint: `/CreateDocumentComment/${documentShortCode}`, payload: requestPayload })).data;
}

async function updateDocumentComment (documentShortCode: string, commentId: string, payload: IDocumentCommentUpdateRequest): Promise<IDocumentComment> {
    const requestPayload = await prepareRichTextImagePayload(payload);
    return (await apiCall.put<typeof requestPayload, IDocumentComment>({ endpoint: `/UpdateDocumentComment/${documentShortCode}/${commentId}`, payload: requestPayload })).data;
}

async function voteDocumentComment (documentShortCode: string, commentId: string, payload: IDocumentCommentVoteRequest): Promise<IDocumentComment> {
    return (await apiCall.post<IDocumentCommentVoteRequest, IDocumentComment>({ endpoint: `/VoteDocumentComment/${documentShortCode}/${commentId}`, payload })).data;
}

async function deleteDocumentComment (documentShortCode: string, commentId: string): Promise<void> {
    await apiCall.delete<string, void>({ endpoint: `/DeleteDocumentComment/${documentShortCode}/${commentId}`, payload: '' });
}

async function getPdfDetails (documentShortCode: string): Promise<IPdfDetails> {
    const endpoint = authState.value.loggedIn ? `/GetPdfDetails/${documentShortCode}` : `/GetPdfDetailsPublic/${documentShortCode}`;
    return (await apiCall.get<IPdfDetails>({ endpoint })).data;
}

async function getPowerBiDetails (documentShortCode: string): Promise<IPowerBiDetails> {
    const endpoint = authState.value.loggedIn ? `/GetPowerBiDetails/${documentShortCode}` : `/GetPowerBiDetailsPublic/${documentShortCode}`;
    return (await apiCall.get<IPowerBiDetails>({ endpoint })).data;
}

async function getExcelDetails (documentShortCode: string): Promise<IExcelDetails> {
    const endpoint = authState.value.loggedIn ? `/GetExcelDetails/${documentShortCode}` : `/GetExcelDetailsPublic/${documentShortCode}`;
    return (await apiCall.get<IExcelDetails>({ endpoint })).data;
}

async function getSparkContentDetails (documentShortCode: string): Promise<ISparkContentDetails> {
    const endpoint = authState.value.loggedIn ? `/GetSparkContentDetails/${documentShortCode}` : `/GetSparkContentDetailsPublic/${documentShortCode}`;
    return (await apiCall.get<ISparkContentDetails>({ endpoint })).data;
}

async function getSparkDefinitionSearchInfo (): Promise<Array<ISparkDefinitionSearchInfo>> {
    const endpoint = authState.value.loggedIn ? '/GetSparkDefinitionSearchInfo' : '/GetSparkDefinitionSearchInfoPublic';
    return (await apiCall.get<Array<ISparkDefinitionSearchInfo>>({ endpoint })).data;
}

async function getAllAdminPDFDocumentInfo (): Promise<Array<IAdminPDFDocument>> {
    const endpoint = '/getAllAdminPDFDocumentInfo';
    return (await apiCall.get<Array<IAdminPDFDocument>>({ endpoint })).data;
}

async function getAllAdminPowerBiInfo (): Promise<Array<IAdminPowerBi>> {
    const endpoint = '/getAllAdminPowerBiInfo';
    return (await apiCall.get<Array<IAdminPowerBi>>({ endpoint })).data;
}

async function getAllAdminExcelDocumentInfo (): Promise<Array<IAdminExcelDocument>> {
    const endpoint = '/getAllAdminExcelDocumentInfo';
    return (await apiCall.get<Array<IAdminExcelDocument>>({ endpoint })).data;
}

async function getAllAdminSparkContentInfo (): Promise<Array<IAdminSparkContent>> {
    const endpoint = '/GetAllAdminSparkContentInfo';
    return (await apiCall.get<Array<IAdminSparkContent>>({ endpoint })).data;
}

async function getAllAnalysts (): Promise<Array<IAnalystAdmin>> {
    const endpoint = '/GetAllAnalystDetails';
    return (await apiCall.get<Array<IAnalystAdmin>>({ endpoint })).data;
}

async function getAnalystImage (analystId: string): Promise<Uint8Array | null> {
    const endpoint = `/GetAnalystImage/${analystId}`;
    const response = await apiCall.get<Uint8Array>({
        endpoint,
        extraScopeOptions: { responseType: 'arrayBuffer' },
    });
    if (response.status === 204) {
        return null;
    }
    return new Uint8Array(response.data);
}

async function uploadAnalystImage (formData: FormData): Promise<void> {
    const endpoint = '/UploadAnalystImage';
    await apiCall.post<FormData, void>({ endpoint, payload: formData, extraScopeOptions: { requestType: 'form-data' } });
}

async function uploadAnalystDetails (analyst: IAnalystAdmin | ICreateAnalyst): Promise<void> {
    const endpoint = '/UploadAnalystDetails';
    await apiCall.post<IAnalystAdmin | ICreateAnalyst, void>({ endpoint, payload: analyst });
}

async function getEmbeddingsSearch (query: string, type: string, documentIds?: Array<string>): Promise<Array<string>> {
    const endpoint = '/EmbeddingsSearch';
    const payload = { query, type, documentIds: documentIds ?? null };
    return (await apiCall.post<typeof payload, Array<string>>({ endpoint, payload })).data;
}

async function getUserSettings (): Promise<IDeltaUserSettings> {
    const endpoint = '/GetUserSettings';
    return (await apiCall.get<IDeltaUserSettings>({ endpoint })).data;
}

async function updateUserSettings (settings: IDeltaUserSettings): Promise<IDeltaUserSettings> {
    const endpoint = '/UpdateUserSettings';
    return (await apiCall.post<IDeltaUserSettings, IDeltaUserSettings>({ endpoint, payload: settings })).data;
}

async function getUserTrackingData (): Promise<IUserTrackingData> {
    const endpoint = '/GetUserTrackingData';
    return (await apiCall.get<IUserTrackingData>({ endpoint })).data;
}

async function addViewedDocumentShortCode (documentShortCode: string): Promise<void> {
    const endpoint = `/AddViewedDocumentShortCode/${encodeURIComponent(documentShortCode)}`;
    await apiCall.post<string, void>({ endpoint, payload: '' });
}

async function updateFeedVisitState (feedVisitState: IFeedVisitState): Promise<void> {
    const endpoint = '/UpdateFeedVisitState';
    await apiCall.put<IFeedVisitState, void>({ endpoint, payload: feedVisitState });
}

async function postAiResponseFeedback (feedback: IFeedback): Promise<void> {
    const endpoint = '/SaveAiResponseFeedback';
    await apiCall.post<IFeedback, void>({ endpoint, payload: feedback });
}

async function getTransposeDetails (): Promise<ITransposeAccessDetails> {
    const endpoint = '/GetTransposeDetails';
    return (await apiCall.get<ITransposeAccessDetails>({ endpoint })).data;
}

async function trackTransposeRequestHistory (options: IGenerationOptionWithDocumentCode): Promise<void> {
    const endpoint = '/TrackTransposeRequestHistory';
    await apiCall.post<IGenerationOption, void>({ endpoint, payload: options });
}

async function acceptTermsAndConditions (): Promise<void> {
    const endpoint = '/userAccess/acceptAiTerms';
    await accessApiCall.get<void>({ endpoint });
}

async function acceptAiWarning (): Promise<void> {
    const endpoint = '/userAccess/acceptAiWarning';
    await accessApiCall.get<void>({ endpoint });
}

async function getExperts (subscriptions: IGetExpertsPayload): Promise<Array<IExpert>> {
    const endpoint = '/GetExperts';
    return (await apiCall.post<IGetExpertsPayload, Array<IExpert>>({ endpoint, payload: subscriptions })).data;
}

async function contactOurExperts (chatInfo: IChatToExpertsPayload): Promise<void> {
    const endpoint = authState.value.loggedIn ? '/ChatToExperts' : '/ChatToExpertsPublic';
    await apiCall.post<IChatToExpertsPayload, void>({ endpoint, payload: chatInfo });
}

async function shareDocument (chatInfo: IShareDocumentPayload): Promise<string> {
    const endpoint = '/CreateShareLink';
    return (await apiCall.post<IShareDocumentPayload, string>({ endpoint, payload: chatInfo })).data;
}

async function getSharedDocumentInfo (shareId: string): Promise<ISharedDocumentInfo | null> {
    const endpoint = authState.value.loggedIn ? `/GetSharedDocumentInfo/${shareId}` : `/GetSharedDocumentInfoPublic/${shareId}`;
    return (await apiCall.get<ISharedDocumentInfo | null>({ endpoint })).data;
}

async function refreshUserAccess (): Promise<void> {
    const endpoint = '/RefreshUserAccess';
    await apiCall.get<void>({ endpoint });
}

async function logDocumentInteraction (documentShortCode: string): Promise<void> {
    const endpoint = '/documentInteraction';
    await apiCall.post<{ documentShortCode: string; interactionType: string }, void>({
        endpoint,
        payload: { documentShortCode, interactionType: 'DownloadProduct' },
    });
}

async function getExcelFile (documentShortCode: string): Promise<ArrayBuffer> {
    const endpoint = `/GetExcelFile/${documentShortCode}`;
    return (await apiCall.get<ArrayBuffer>({ endpoint, extraScopeOptions: { responseType: 'arrayBuffer' } })).data;
}

async function uploadPDF (formData: FormData): Promise<void> {
    const endpoint = '/UploadPDF';
    await apiCall.post<FormData, void>({ endpoint, payload: formData, extraScopeOptions: { requestType: 'form-data' } });
}

async function reuploadPDF (formData: FormData): Promise<void> {
    const endpoint = '/ReuploadPDF';
    await apiCall.post<FormData, void>({ endpoint, payload: formData, extraScopeOptions: { requestType: 'form-data' } });
}

async function deletePdf (documentShortCode: string): Promise<void> {
    const endpoint = `/DeletePdf/${documentShortCode}`;
    await apiCall.delete<string, void>({ endpoint, payload: '' });
}

async function deleteExcel (documentShortCode: string): Promise<void> {
    const endpoint = `/DeleteExcel/${documentShortCode}`;
    await apiCall.delete<string, void>({ endpoint, payload: '' });
}

async function deletePowerBi (documentShortCode: string): Promise<void> {
    const endpoint = `/DeletePowerBi/${documentShortCode}`;
    await apiCall.delete<string, void>({ endpoint, payload: '' });
}

async function uploadExcel (formData: FormData): Promise<void> {
    const endpoint = '/UploadExcel';
    await apiCall.post<FormData, void>({ endpoint, payload: formData, extraScopeOptions: { requestType: 'form-data' } });
}

async function uploadSparkContent (formData: FormData): Promise<string> {
    const endpoint = '/UploadSparkContent';
    return (await apiCall.post<FormData, string>({ endpoint, payload: formData, extraScopeOptions: { requestType: 'form-data' } })).data;
}

async function uploadSparkContentWithImage (document: IAdminSparkContent, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(await prepareRichTextImagePayload(document)));
    return uploadSparkContent(formData);
}

async function addOrUpdatePDFDocumentAdmin (document: IAdminPDFDocument): Promise<void> {
    const endpoint = '/AddOrUpdatePDFDocumentAdmin';
    await apiCall.post<IAdminPDFDocument, void>({ endpoint, payload: document });
}

async function addOrUpdatePowerBiAdmin (document: IAdminPowerBi): Promise<void> {
    const endpoint = '/AddOrUpdatePowerBiAdmin';
    await apiCall.post<IAdminPowerBi, void>({ endpoint, payload: document });
}

async function addOrUpdateExcelDocumentAdmin (document: IAdminExcelDocument): Promise<void> {
    const endpoint = '/AddOrUpdateExcelDocumentAdmin';
    await apiCall.post<IAdminExcelDocument, void>({ endpoint, payload: document });
}

async function addOrUpdateSparkContentAdmin (document: IAdminSparkContent): Promise<string> {
    const endpoint = '/AddOrUpdateSparkContentAdmin';
    const requestPayload = await prepareRichTextImagePayload(document);
    return (await apiCall.post<typeof requestPayload, string>({ endpoint, payload: requestPayload })).data;
}

async function getAllAccessLevelsInformation (): Promise<Array<IAdminAccessInfo>> {
    const endpoint = '/AccessManagement/GetAllAccessLevelsInformation';
    return (await accessApiCall.get<Array<IAdminAccessInfo>>({ endpoint })).data;
}

async function addAccessLevel (accessLevel: IAdminAccessInfo): Promise<void> {
    const endpoint = '/AccessManagement/AddAccessLevel';
    await accessApiCall.post<IAdminAccessInfo, void>({ endpoint, payload: accessLevel });
}

async function updateAccessLevel (accessLevel: IAdminAccessInfo): Promise<void> {
    const endpoint = '/AccessManagement/UpdateAccessLevel';
    await accessApiCall.put<IAdminAccessInfo, void>({ endpoint, payload: accessLevel });
}

async function deleteAccessLevel (id: string): Promise<void> {
    const deltaGptEndpoint = '/RemoveAccessLevelFromDocuments';
    await apiCall.post<string, void>({ endpoint: deltaGptEndpoint, payload: id }); // Remove references to the access level in reports first

    const accessEndpoint = '/AccessManagement/DeleteAccessLevel';
    await accessApiCall.delete<string, void>({ endpoint: accessEndpoint, payload: id });
}

async function deleteSubAccessLevel (accessLevelId: string, subAccessLevelId: string): Promise<void> {
    const deltaGptEndpoint = '/RemoveSubAccessLevelFromDocuments';
    await apiCall.post<string, void>({ endpoint: deltaGptEndpoint, payload: subAccessLevelId }); // Remove references to the access level in reports first

    const accessEndpoint = '/AccessManagement/DeleteSubAccessLevel';
    await accessApiCall.delete<{accessLevelId: string; subAccessLevelId: string}, void>({ endpoint: accessEndpoint, payload: { accessLevelId, subAccessLevelId } });
}

async function bulkAddDocumentAccess (payload: IBulkDocumentAccessPayload): Promise<void> {
    const endpoint = '/BulkAddDocumentAccess';
    await apiCall.post<IBulkDocumentAccessPayload, void>({ endpoint, payload });
}

async function bulkDeleteDocumentAccess (payload: IBulkDocumentAccessPayload): Promise<void> {
    const endpoint = '/BulkDeleteDocumentAccess';
    await apiCall.post<IBulkDocumentAccessPayload, void>({ endpoint, payload });
}

async function getHomepageExperts (homepage: string): Promise<Array<IAnalyst>> {
    const endpoint = `/GetHomepageExperts/${homepage}`;
    return (await apiCall.get<Array<IAnalyst>>({ endpoint })).data;
}

async function getMostViewedReports (): Promise<Array<string>> {
    const endpoint = '/MostViewedReports';
    return (await apiCall.get<Array<string>>({ endpoint })).data;
}

async function getMostViewedReportsForUser (): Promise<Array<string>> {
    const endpoint = '/MostViewedReportsForUser';
    return (await apiCall.get<Array<string>>({ endpoint })).data;
}

async function getFilterOrder (siteArea: string) :Promise<IDocumentFilterOrderResponse> {
    const endpoint = `/GetFilterOrder/${siteArea}`;
    return (await apiCall.get<IDocumentFilterOrderResponse>({ endpoint })).data;
}

async function getSubscriptionFilters () :Promise<Array<string>> {
    const endpoint = '/GetSubscriptionFilterIds';
    return (await apiCall.get<Array<string>>({ endpoint })).data;
}

async function getUserAccess (): Promise<IResearchAccess> {
    const endpoint = '/GetUserConfig';
    return (await apiCall.get<IResearchAccess>({ endpoint })).data;
}

async function getMyAccess (): Promise<IUserAccessSummaryResponse> {
    const endpoint = '/my-access';
    return (await apiCall.get<IUserAccessSummaryResponse>({ endpoint })).data;
}

async function getDocumentShortcodeFromSubPortalId (subPortalId: string): Promise<string | null> {
    const endpoint = `/GetDocumentFromSubPortalId/${subPortalId}`;
    return (await apiCall.get<string>({ endpoint })).data;
}

async function deleteSparkContent (documentShortCode: string): Promise<void> {
    const endpoint = `/DeleteSparkContent/${documentShortCode}`;
    await apiCall.delete<string, void>({ endpoint, payload: '' });
}

function buildReportTypeQuery (reportTypes?: Array<ReportType>): string {
    if (!reportTypes?.length) return '';
    const params = new URLSearchParams();
    reportTypes.forEach(reportType => params.append('reportTypes', reportType));
    return params.toString();
}

async function prepareRichTextImagePayload<TPayload extends IRichTextPayloadWithPendingImages> (
    payload: TPayload,
): Promise<Omit<TPayload, 'richTextImages'> & IRichTextPayloadWithUploadImages> {
    const { richTextImages, ...payloadWithoutImages } = payload;
    if (!richTextImages?.length) return payloadWithoutImages;
    const payloadWithImageMarkers = replacePendingImageSourcesWithMarkers(payloadWithoutImages, richTextImages);

    return {
        ...payloadWithImageMarkers,
        richTextImages: await Promise.all(richTextImages.map(createRichTextImageUploadPayload)),
    };
}

async function createRichTextImageUploadPayload (image: IRichTextPendingImage): Promise<IRichTextImageUploadPayload> {
    return {
        imageId: image.imageId,
        fileName: image.file.name,
        contentType: image.file.type,
        base64Data: await fileToBase64(image.file),
    };
}

function replacePendingImageSourcesWithMarkers<TPayload> (payload: TPayload, images: Array<IRichTextPendingImage>): TPayload {
    return Object.fromEntries(Object.entries(payload as Record<string, unknown>).map(([key, value]) => [
        key,
        typeof value === 'string'
            ? replacePendingImageSourcesInHtml(value, images)
            : value,
    ])) as TPayload;
}

function replacePendingImageSourcesInHtml (html: string, images: Array<IRichTextPendingImage>): string {
    return images.reduce(
        (nextHtml, image) => nextHtml.replaceAll(image.temporaryUrl, getRichTextImageMarker(image.imageId)),
        html,
    );
}

function getRichTextImageMarker (imageId: string): string {
    return `__rich_text_image_${imageId}__`;
}

function fileToBase64 (file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (): void => {
            const result = reader.result?.toString() ?? '';
            resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = (): void => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

export const deltaGptApi = {
    crossDocumentChat,
    documentChat,
    getDocumentReferences,
    getPageReferences,
    getPdf,
    getPdfPage,
    getAllDocumentInfo,
    getAllDocumentInfoByType,
    getDocumentComments,
    createDocumentComment,
    updateDocumentComment,
    voteDocumentComment,
    deleteDocumentComment,
    getAllAdminPDFDocumentInfo,
    getAllAdminPowerBiInfo,
    getAllAdminExcelDocumentInfo,
    getAllAdminSparkContentInfo,
    getAllAccessLevelsInformation,
    getEmbeddingsSearch,
    getUserSettings,
    updateUserSettings,
    getUserTrackingData,
    addViewedDocumentShortCode,
    updateFeedVisitState,
    postAiResponseFeedback,
    getTransposeDetails,
    trackTransposeRequestHistory,
    acceptTermsAndConditions,
    acceptAiWarning,
    getExperts,
    contactOurExperts,
    shareDocument,
    getSharedDocumentInfo,
    refreshUserAccess,
    logDocumentInteraction,
    getPdfDetails,
    getPowerBiDetails,
    getExcelDetails,
    getSparkContentDetails,
    getSparkDefinitionSearchInfo,
    getExcelFile,
    uploadPDF,
    reuploadPDF,
    uploadExcel,
    uploadSparkContent,
    uploadSparkContentWithImage,
    addOrUpdatePDFDocumentAdmin,
    addOrUpdateExcelDocumentAdmin,
    addOrUpdatePowerBiAdmin,
    addOrUpdateSparkContentAdmin,
    getAllAnalysts,
    uploadAnalystImage,
    uploadAnalystDetails,
    getAnalystImage,
    addAccessLevel,
    updateAccessLevel,
    deleteAccessLevel,
    getHomepageExperts,
    getMostViewedReports,
    getMostViewedReportsForUser,
    getFilterOrder,
    getSubscriptionFilters,
    deleteSubAccessLevel,
    bulkAddDocumentAccess,
    bulkDeleteDocumentAccess,
    getUserAccess,
    getMyAccess,
    deletePdf,
    deleteExcel,
    deletePowerBi,
    deleteSparkContent,
    getDocumentShortcodeFromSubPortalId,
};
